import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'

interface AutoConfigureInitResponse {
  auth_url: string
  session_id: string
  provider_name: string
}

interface AutoConfigureStatusResponse {
  status: 'pending' | 'complete' | 'failed' | 'expired'
  records_created: number
  error_message: string | null
  domain_status: string
}

/**
 * Initiates auto-configure flow. Returns the auth URL to open in a popup.
 */
export function useAutoConfigureInit(): ReturnType<
  typeof useMutation<AutoConfigureInitResponse, unknown, { domainId: string; provider: string }>
> {
  return useMutation({
    mutationFn: ({ domainId, provider }: { domainId: string; provider: string }) =>
      apiClient.post<AutoConfigureInitResponse>(`/v1/domains/${domainId}/auto-configure/init`, {
        provider,
      }),
  })
}

/**
 * Polls auto-configure session status. Enabled when session_id is set.
 * Polls every 2 seconds as fallback (primary detection via postMessage).
 */
export function useAutoConfigureStatus(
  domainId: string,
  sessionId: string | null,
  enabled: boolean,
): ReturnType<typeof useQuery<AutoConfigureStatusResponse>> {
  return useQuery({
    queryKey: ['auto-configure-status', domainId, sessionId],
    queryFn: () =>
      apiClient.get<AutoConfigureStatusResponse>(
        `/v1/domains/${domainId}/auto-configure/status?session_id=${sessionId}`,
      ),
    enabled: enabled && !!sessionId,
    refetchInterval: 2000,
  })
}

export type { AutoConfigureInitResponse, AutoConfigureStatusResponse }
